import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Users, Calendar, ListChecks, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, Clock, CheckCircle, User, Briefcase,
  CalendarDays, Search, Filter, LayoutGrid, List,
  GanttChart, CheckSquare, UserPlus, X, ArrowLeft,
  MessageSquare
} from 'lucide-react';
import TaskComments from '../components/TaskComments';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-800',
  'Medium': 'bg-blue-100 text-blue-800',
  'High': 'bg-orange-100 text-orange-800',
  'Urgent': 'bg-red-100 text-red-800'
};

const PRIORITY_BORDER_COLORS = {
  'Low': 'border-l-gray-400',
  'Medium': 'border-l-blue-500',
  'High': 'border-l-orange-500',
  'Urgent': 'border-l-red-500'
};

const STATUS_COLORS = {
  'To Do': 'bg-slate-200 text-slate-700 border border-slate-300',
  'In Progress': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Completed': 'bg-green-100 text-green-800 border border-green-200',
  'On Hold': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  'Cancelled': 'bg-red-100 text-red-800 border border-red-200'
};

const STATUS_DOT_COLORS = {
  'To Do': 'bg-slate-400',
  'Upcoming': 'bg-slate-400',
  'In Progress': 'bg-blue-500',
  'Completed': 'bg-green-500',
  'On Hold': 'bg-yellow-500',
  'Cancelled': 'bg-red-400'
};

const KANBAN_COLUMNS = ['Upcoming', 'In Progress', 'On Hold', 'Completed'];

const getKanbanStatus = (status) => (status === 'To Do' ? 'Upcoming' : status);

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TeamCalendar = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamOverview, setTeamOverview] = useState({ subordinates: [], summary: {} });
  const [memberTasks, setMemberTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [memberSearch, setMemberSearch] = useState('');

  const { getAuthHeaders, currentOffice, isOfficeAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTeamData();
  }, [currentOffice]);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberTasks(selectedMember.id, taskFilter);
    }
  }, [selectedMember, taskFilter]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const [membersRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks/subordinates`, { headers }),
        fetch(`${API_URL}/api/tasks/team-overview`, { headers })
      ]);

      if (membersRes.ok) setTeamMembers(await membersRes.json());
      if (overviewRes.ok) setTeamOverview(await overviewRes.json());
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberTasks = async (employeeId, filter = 'all') => {
    setLoadingTasks(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const filterParam = filter !== 'all' ? `?task_filter=${filter}` : '';
      const res = await fetch(`${API_URL}/api/tasks/subordinate/${employeeId}/tasks${filterParam}`, { headers });
      if (res.ok) setMemberTasks(await res.json());
    } catch (error) {
      console.error('Error fetching member tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setMemberTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
        if (selectedTask?.id === taskId) setSelectedTask({ ...selectedTask, status: newStatus });
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredTasks = memberTasks.filter(task => {
    const matchesSearch = task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const tasksByStatus = KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col] = filteredTasks.filter(t => getKanbanStatus(t.status) === col);
    return acc;
  }, {});

  const stats = {
    total: memberTasks.length,
    pending: memberTasks.filter(t => !['Completed', 'Cancelled'].includes(t.status)).length,
    completed: memberTasks.filter(t => t.status === 'Completed').length,
    overdue: memberTasks.filter(t => t.is_overdue).length
  };

  const filteredMembers = teamMembers.filter(m =>
    m.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.job_title?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  // ── Kanban Card ──
  const KanbanTaskCard = ({ task }) => {
    const completedItems = task.checklist?.filter(i => i.completed).length || 0;
    const totalItems = task.checklist?.length || 0;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.15 }}
        onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
        className={`p-3 bg-white dark:bg-gray-800 rounded-lg border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]} border border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer hover:shadow-lg transition-all`}
        data-testid={`team-kanban-task-${task.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[task.priority]}`}>
            {task.priority}
          </span>
          {task.is_overdue && <Badge className="bg-red-100 text-red-800 text-[10px]">Overdue</Badge>}
        </div>
        <h4 className="font-medium text-sm mb-1 line-clamp-2">{task.title}</h4>
        {task.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{task.description}</p>}
        {totalItems > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <CheckSquare className="w-3 h-3" />
              <span>{completedItems}/{totalItems}</span>
            </div>
            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 transition-all" style={{ width: `${(completedItems / totalItems) * 100}%` }} />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          {task.due_date ? (
            <span className={`flex items-center gap-1 text-xs ${task.is_overdue ? 'text-red-500' : 'text-gray-500'}`}>
              <Calendar className="w-3 h-3" />
              {formatDate(task.due_date)}
            </span>
          ) : <div />}
          {task.assigned_to_name && (
            <Avatar className="h-5 w-5 border border-white">
              <AvatarFallback className="text-[9px] bg-blue-100 text-blue-700">{getInitials(task.assigned_to_name)}</AvatarFallback>
            </Avatar>
          )}
        </div>
      </motion.div>
    );
  };

  // ── Kanban Column ──
  const KanbanColumn = ({ status, tasks: columnTasks }) => (
    <div className="flex-1 min-w-[220px] max-w-[280px] rounded-xl p-3 bg-gray-50 dark:bg-gray-900 border-2 border-blue-200 dark:border-blue-800" data-testid={`team-kanban-col-${status.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_COLORS[status]}`} />
        <h3 className="font-semibold text-sm">{status}</h3>
        <span className="text-xs text-gray-500 bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded-full border">{columnTasks.length}</span>
      </div>
      <div className="space-y-2 min-h-[120px] max-h-[480px] overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {columnTasks.map(task => <KanbanTaskCard key={task.id} task={task} />)}
        </AnimatePresence>
        {columnTasks.length === 0 && (
          <div className="flex items-center justify-center h-[80px] text-gray-400 text-xs border-2 border-dashed rounded-lg border-gray-200">No tasks</div>
        )}
      </div>
    </div>
  );

  // ── Calendar View ──
  const CalendarView = () => {
    const days = useMemo(() => {
      const y = calendarDate.getFullYear(), m = calendarDate.getMonth();
      const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
      const arr = [];
      for (let i = 0; i < first.getDay(); i++) arr.push(null);
      for (let i = 1; i <= last.getDate(); i++) arr.push(new Date(y, m, i));
      return arr;
    }, [calendarDate]);

    const getTasksForDate = (date) => {
      if (!date) return [];
      const ds = date.toISOString().split('T')[0];
      return filteredTasks.filter(t => t.due_date?.split('T')[0] === ds);
    };

    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    return (
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setCalendarDate(d => { const n = new Date(d); n.setMonth(n.getMonth()-1); return n; })}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="text-lg">{months[calendarDate.getMonth()]} {calendarDate.getFullYear()}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setCalendarDate(d => { const n = new Date(d); n.setMonth(n.getMonth()+1); return n; })}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(d => <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, i) => {
              const dayTasks = getTasksForDate(date);
              const isToday = date && date.toDateString() === new Date().toDateString();
              return (
                <div key={i} className={`min-h-[90px] p-1 rounded-lg border transition-all ${!date ? 'bg-gray-50 border-transparent' : isToday ? 'bg-blue-50 border-blue-400' : 'bg-white border-gray-200 hover:border-blue-300'}`}>
                  {date && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>{date.getDate()}</div>
                      <div className="space-y-1">
                        {dayTasks.slice(0, 3).map(task => (
                          <div key={task.id} onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer ${task.status === 'Completed' ? 'bg-green-100 text-green-800 line-through' : task.is_overdue ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                            {task.title}
                          </div>
                        ))}
                        {dayTasks.length > 3 && <div className="text-[10px] text-gray-500 px-1">+{dayTasks.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── Timeline View ──
  const TimelineView = () => {
    const tasksByDate = filteredTasks.filter(t => t.due_date).sort((a, b) => new Date(a.due_date) - new Date(b.due_date)).reduce((acc, t) => {
      const k = t.due_date.split('T')[0];
      (acc[k] = acc[k] || []).push(t);
      return acc;
    }, {});
    const noDueTasks = filteredTasks.filter(t => !t.due_date);
    const sortedDates = Object.keys(tasksByDate).sort();

    const fmtDate = (ds) => {
      const d = new Date(ds), today = new Date(), tmrw = new Date(today); tmrw.setDate(tmrw.getDate() + 1);
      if (d.toDateString() === today.toDateString()) return 'Today';
      if (d.toDateString() === tmrw.toDateString()) return 'Tomorrow';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    return (
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardHeader><CardTitle className="flex items-center gap-2"><GanttChart className="w-5 h-5" />Timeline</CardTitle></CardHeader>
        <CardContent>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200" />
            <div className="space-y-6">
              {sortedDates.map(ds => {
                const dayTasks = tasksByDate[ds];
                const isOverdue = new Date(ds) < new Date(new Date().toDateString()) && dayTasks.some(t => t.status !== 'Completed');
                return (
                  <div key={ds} className="relative pl-10">
                    <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${isOverdue ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <div className={`text-sm font-semibold mb-2 ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
                      {fmtDate(ds)}{isOverdue && <span className="ml-2 text-xs font-normal">(Overdue)</span>}
                    </div>
                    <div className="space-y-2">
                      {dayTasks.map(task => (
                        <div key={task.id} onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                          className={`p-3 rounded-lg border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]} bg-white border border-gray-200 cursor-pointer hover:shadow-md transition-all`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {task.status === 'Completed' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <div className={`w-4 h-4 rounded-full border-2 ${STATUS_DOT_COLORS[task.status]?.replace('bg-', 'border-')}`} />}
                              <span className={`font-medium ${task.status === 'Completed' ? 'line-through text-gray-400' : ''}`}>{task.title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                              <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {noDueTasks.length > 0 && (
                <div className="relative pl-10">
                  <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-gray-400" />
                  <div className="text-sm font-semibold mb-2 text-gray-500">No Due Date</div>
                  <div className="space-y-2">
                    {noDueTasks.map(task => (
                      <div key={task.id} onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                        className={`p-3 rounded-lg border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]} bg-white border border-gray-200 cursor-pointer hover:shadow-md transition-all`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{task.title}</span>
                          <div className="flex items-center gap-2">
                            <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                            <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {sortedDates.length === 0 && noDueTasks.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <GanttChart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No tasks to display in timeline</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── Loading State ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { summary } = teamOverview;

  return (
    <div className="space-y-6" data-testid="my-team-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
        <p className="text-gray-600">
          {isOfficeAdmin && isOfficeAdmin() 
            ? "View and manage tasks of all employees in your office"
            : "View and manage tasks of your direct reports"}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Team Members', value: teamMembers.length, icon: Users, color: 'blue' },
          { label: 'Total Tasks', value: summary.total_tasks || 0, icon: ListChecks, color: 'purple' },
          { label: 'Pending', value: summary.pending || 0, icon: Clock, color: 'yellow' },
          { label: 'Overdue', value: summary.overdue || 0, icon: AlertCircle, color: 'red' },
          { label: 'Completed', value: summary.completed || 0, icon: CheckCircle, color: 'green' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${color}-100 rounded-lg`}>
                  <Icon className={`h-5 w-5 text-${color}-600`} />
                </div>
                <div>
                  <p className="text-xs text-gray-600">{label}</p>
                  <p className={`text-xl font-bold ${color === 'red' && value > 0 ? 'text-red-600' : ''}`}>{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Team Members List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="member-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              {filteredMembers.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No team members found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredMembers.map((member) => {
                    const memberStats = teamOverview.subordinates?.find(s => s.id === member.id)?.tasks || {};
                    return (
                      <div
                        key={member.id}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedMember?.id === member.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                        onClick={() => { setSelectedMember(member); setTaskFilter('all'); setSearchQuery(''); setStatusFilter('all'); }}
                        data-testid={`team-member-${member.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                            {getInitials(member.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm">{member.name}</p>
                            <p className="text-xs text-gray-500 truncate">{member.job_title || 'Employee'}</p>
                          </div>
                        </div>
                        {memberStats.total > 0 && (
                          <div className="mt-2 flex gap-2 text-xs">
                            <span className="text-yellow-600">{memberStats.pending || 0} pending</span>
                            {memberStats.overdue > 0 && (
                              <span className="text-red-600 font-medium">{memberStats.overdue} overdue</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Task View */}
        <Card className="lg:col-span-3">
          {!selectedMember ? (
            <CardContent className="p-12 text-center">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900">Select a Team Member</h3>
              <p className="text-gray-500 mt-1">Click on a team member to view their tasks</p>
            </CardContent>
          ) : (
            <>
              {/* Member Header */}
              <CardHeader className="border-b pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-medium">
                      {getInitials(selectedMember.name)}
                    </div>
                    <div>
                      <CardTitle>{selectedMember.name}</CardTitle>
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        {selectedMember.job_title || 'Employee'}
                        {selectedMember.department && ` · ${selectedMember.department}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {loadingTasks && <Loader2 className="h-5 w-5 animate-spin text-blue-500" />}
                    {/* Mini stats */}
                    <div className="hidden md:flex items-center gap-3 text-sm">
                      <span className="text-gray-500">{stats.total} tasks</span>
                      {stats.overdue > 0 && <span className="text-red-600 font-medium">{stats.overdue} overdue</span>}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-4 space-y-4">
                {/* Task Filter Tabs */}
                <Tabs value={taskFilter} onValueChange={setTaskFilter} className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-3">
                    <TabsTrigger value="all" className="text-xs" data-testid="team-tab-all">
                      <ListChecks className="h-3.5 w-3.5 mr-1" />All
                    </TabsTrigger>
                    <TabsTrigger value="assigned" className="text-xs" data-testid="team-tab-assigned">
                      <UserPlus className="h-3.5 w-3.5 mr-1" />Assigned
                    </TabsTrigger>
                    <TabsTrigger value="created" className="text-xs" data-testid="team-tab-created">
                      <User className="h-3.5 w-3.5 mr-1" />Created
                    </TabsTrigger>
                    <TabsTrigger value="collaboration" className="text-xs" data-testid="team-tab-collab">
                      <Users className="h-3.5 w-3.5 mr-1" />Collab
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Search + Status + View Mode */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search tasks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="team-task-search" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px] h-9" data-testid="team-status-filter">
                      <Filter className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="To Do">To Do</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    {[
                      { mode: 'kanban', icon: LayoutGrid, label: 'Kanban' },
                      { mode: 'list', icon: List, label: 'List' },
                      { mode: 'calendar', icon: CalendarDays, label: 'Calendar' },
                      { mode: 'timeline', icon: GanttChart, label: 'Timeline' },
                    ].map(({ mode, icon: Icon, label }) => (
                      <Button key={mode} variant={viewMode === mode ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode(mode)} title={label} data-testid={`team-view-${mode}`}>
                        <Icon className="w-4 h-4" />
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Task Content */}
                {loadingTasks ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  </div>
                ) : viewMode === 'kanban' ? (
                  <div className="flex gap-3 overflow-x-auto pb-4">
                    {KANBAN_COLUMNS.map(status => (
                      <KanbanColumn key={status} status={status} tasks={tasksByStatus[status] || []} />
                    ))}
                  </div>
                ) : viewMode === 'calendar' ? (
                  <CalendarView />
                ) : viewMode === 'timeline' ? (
                  <TimelineView />
                ) : (
                  /* List View */
                  <div>
                    {filteredTasks.length > 0 ? (
                      <div className="space-y-2">
                        {filteredTasks.map(task => (
                          <div
                            key={task.id}
                            className={`flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow cursor-pointer border-l-4 ${PRIORITY_BORDER_COLORS[task.priority]}`}
                            onClick={() => { setSelectedTask(task); setShowDetailModal(true); }}
                            data-testid={`team-task-item-${task.id}`}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-sm">{task.title}</p>
                                  {task.is_overdue && <Badge className="bg-red-100 text-red-800 text-xs">Overdue</Badge>}
                                </div>
                                {task.description && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>}
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  {task.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  )}
                                  {task.created_by_name && (
                                    <span className="flex items-center gap-1">
                                      <User className="h-3 w-3" />
                                      By: {task.created_by_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              <Badge className={`${PRIORITY_COLORS[task.priority]} text-xs`}>{task.priority}</Badge>
                              <Badge className={`${STATUS_COLORS[task.status]} text-xs`}>{task.status}</Badge>
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <ListChecks className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No tasks found</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Task Detail Modal with Comments */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setSelectedTask(null); }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="team-task-detail-modal">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedTask.title}
                  {selectedTask.is_overdue && <Badge className="bg-red-100 text-red-800">Overdue</Badge>}
                </DialogTitle>
                <DialogDescription className="sr-only">Task details</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Status + Priority + Due Date */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-500 text-xs">Status</Label>
                    <Select
                      value={selectedTask.status}
                      onValueChange={(v) => {
                        setSelectedTask({ ...selectedTask, status: v });
                        handleStatusUpdate(selectedTask.id, v);
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="team-task-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="To Do">To Do</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Priority</Label>
                    <div className="mt-1">
                      <Badge className={`${PRIORITY_COLORS[selectedTask.priority]} text-sm`}>{selectedTask.priority}</Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Due Date</Label>
                    <div className="mt-1">
                      {selectedTask.due_date ? (
                        <span className={`text-sm ${selectedTask.is_overdue ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                          {new Date(selectedTask.due_date).toLocaleDateString()}
                          {selectedTask.is_overdue && ' (Overdue)'}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Not set</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedTask.description && (
                  <div>
                    <Label className="text-gray-500 text-xs">Description</Label>
                    <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{selectedTask.description}</p>
                  </div>
                )}

                {/* Assigned / Created info */}
                <div className="grid grid-cols-2 gap-3">
                  {selectedTask.assigned_to_name && (
                    <div>
                      <Label className="text-gray-500 text-xs">Assigned To</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs bg-blue-100 text-blue-700">{getInitials(selectedTask.assigned_to_name)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{selectedTask.assigned_to_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedTask.created_by_name && (
                    <div>
                      <Label className="text-gray-500 text-xs">Created By</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs bg-gray-100 text-gray-700">{getInitials(selectedTask.created_by_name)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{selectedTask.created_by_name}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Checklist */}
                {selectedTask.checklist?.length > 0 && (
                  <div>
                    <Label className="text-gray-500 text-xs">Checklist ({selectedTask.checklist.filter(i => i.completed).length}/{selectedTask.checklist.length})</Label>
                    <div className="mt-2 space-y-2">
                      {selectedTask.checklist.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 text-sm">
                          {item.completed ? (
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <div className="h-4 w-4 rounded border-2 border-gray-300 flex-shrink-0" />
                          )}
                          <span className={item.completed ? 'line-through text-gray-400' : ''}>{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="h-4 w-4 text-gray-500" />
                    <Label className="text-gray-500 text-xs font-medium">Comments</Label>
                  </div>
                  <TaskComments taskId={selectedTask.id} task={selectedTask} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamCalendar;
